import { basicPathDownloadND } from '../basicPathND';
import { PDFDocument } from 'pdf-lib';
import { appendLogo, appendSquare, appendStudentName, addFlagWarningPage, splitAndZip } from '../../components/pdf';
import { openPopup } from '../../components/functions';


// function downloadItem(url, fileName){
//     const link = document.createElement('a');
//     link.href = url;
//     link.setAttribute('download', `${fileName}.pdf`);

//     document.body.appendChild(link);
//     link.click();
//     document.body.removeChild(link);
// }

export const fetchMPLessonsSingleND = async (fileInfo) => {
    // Detect portal from domain - use /rp-files on ReadingPlex, /mp-files on MathPlex
    const hostname = window.location.hostname;
    const path = hostname.includes('readingplex') ? '/rp-files' : '/mp-files';
    const reqType = 'get';

    const options = fileInfo.options;
    const params = { fileInfo };

    const graphicParams = {
        fileInfo: {
            fileName: 'MP Outline Light@4x.png',
            type: 'graphic'
        }
    };

    const res = await basicPathDownloadND(params, path, reqType);
    const resGraphic = await basicPathDownloadND(graphicParams, path, reqType);

    // Check if response is valid PDF
    if (!res || !res.data) {
        console.error('[MP_FILES] No data returned from /mp-files endpoint');
        alert('Error: Could not load file. The file may not exist or there was a server error.');
        return;
    }

    // Check if response is actually a PDF (starts with PDF header)
    // Handle both ArrayBuffer and string responses
    let dataView;
    if (res.data instanceof ArrayBuffer) {
        dataView = new Uint8Array(res.data);
    } else if (res.data instanceof Uint8Array) {
        dataView = res.data;
    } else {
        // Response might be HTML (SPA fallback) or text error
        console.error('[MP_FILES] Invalid response data type:', typeof res.data, res.data?.constructor?.name);
        try {
            const textData = typeof res.data === 'string' ? res.data : new TextDecoder().decode(new Uint8Array(res.data));
            if (textData.includes('<!doctype') || textData.includes('<html')) {
                alert('Error: Server routing issue. The request was redirected to the web page instead of the API. Please contact support.');
            } else if (textData.includes('"message"') || textData.includes('error')) {
                const errorObj = JSON.parse(textData);
                alert(`Error loading file: ${errorObj.message || 'File not found or server error'}`);
            } else {
                alert('Error: The server returned an invalid response. Please try again.');
            }
        } catch (e) {
            alert('Error: Could not load file. Please check the console for details.');
        }
        return;
    }

    const pdfHeader = new TextDecoder().decode(dataView.slice(0, 4));
    if (pdfHeader !== '%PDF') {
        console.error('[MP_FILES] Response is not a PDF. Status:', res.status, 'Data preview:', pdfHeader);
        // Try to decode as text to see error message
        try {
            const textData = new TextDecoder().decode(dataView);
            if (textData.includes('<!doctype') || textData.includes('<html')) {
                alert('Error: Server routing issue. The request was redirected to the web page instead of the API. This usually means the Nginx proxy is not configured correctly for /mp-files.');
            } else if (textData.includes('"message"') || textData.includes('error')) {
                const errorObj = JSON.parse(textData);
                alert(`Error loading file: ${errorObj.message || 'File not found or server error'}`);
            } else {
                alert('Error: The server returned an invalid response. Please try again.');
            }
        } catch (e) {
            alert('Error: Could not load file. Please check the console for details.');
        }
        return;
    }

    try {
        let pdfDoc = await PDFDocument.load(res.data);
        if (options.appendName) pdfDoc = await appendStudentName(pdfDoc, options.studentName);
        if (options.appendLogo && resGraphic && resGraphic.data) {
            // Check if graphic is valid
            const graphicView = new Uint8Array(resGraphic.data);
            const graphicHeader = new TextDecoder().decode(graphicView.slice(0, 4));
            if (graphicHeader === '%PDF' || graphicHeader.startsWith('\x89PNG') || graphicHeader.startsWith('GIF8')) {
                pdfDoc = await appendLogo(pdfDoc, resGraphic.data);
            }
        }
        if (options.appendSquare) pdfDoc = await appendSquare(pdfDoc, options.squareColor);

        const pdfData = await pdfDoc.save();
        openPopup(pdfData);
    } catch (error) {
        console.error('[MP_FILES] PDF parsing error:', error);
        alert('Error: Could not process the PDF file. The file may be corrupted.');
    }
}

export const fetchMPLessonsMultiND = async (fileInfos, additionalOptions = {}) => {
    // Detect portal from domain - use /rp-files on ReadingPlex, /mp-files on MathPlex
    const hostname = window.location.hostname;
    const path = hostname.includes('readingplex') ? '/rp-files' : '/mp-files';
    const reqType = 'get';

    const fileData = [];

    if (fileInfos.length > 10) {
        return 'Maximum 10 files can be combined at once.';
    } else if (!fileInfos.length) {
        return 'No files were found';
    }

    for (let fileInfo of fileInfos) {
        const params = { fileInfo };
        const options = fileInfo.options;

        const res = await basicPathDownloadND(params, path, reqType);
        if (!res || !res.data) {
            return `No data returned for file with ID ${fileInfo.lesson_id}`;
        }

        // Check if response is actually a PDF
        let dataView;
        if (res.data instanceof ArrayBuffer) {
            dataView = new Uint8Array(res.data);
        } else if (res.data instanceof Uint8Array) {
            dataView = res.data;
        } else {
            const textData = typeof res.data === 'string' ? res.data : new TextDecoder().decode(new Uint8Array(res.data));
            if (textData.includes('<!doctype') || textData.includes('<html')) {
                return `Server routing error for lesson ID ${fileInfo.lesson_id}. Nginx proxy may not be configured correctly.`;
            }
            return `Invalid response for lesson ID ${fileInfo.lesson_id}. Expected PDF, got: ${textData.substring(0, 50)}`;
        }

        const pdfHeader = new TextDecoder().decode(dataView.slice(0, 4));
        if (pdfHeader !== '%PDF') {
            const textData = new TextDecoder().decode(dataView);
            if (textData.includes('<!doctype') || textData.includes('<html')) {
                return `Server routing error for lesson ID ${fileInfo.lesson_id}. Nginx proxy may not be configured correctly.`;
            }
            return `Invalid PDF file for lesson ID ${fileInfo.lesson_id}. Server returned: ${pdfHeader}`;
        }

        fileData.push({ data: res.data, options });
    }

    const graphicParams = {
        fileInfo: {
            fileName: 'MP Outline Light@4x.png',
            type: 'graphic'
        }
    };
    const resGraphic = await basicPathDownloadND(graphicParams, path, reqType);

    // Track current lesson in order to insert Flag Warning Page appropriately
    let flagPageLessonsOffset = parseInt(additionalOptions.flagPageLessonsOffset);
    let currentLessonIndex = fileData.length - 1;

    if (fileData.length) {
        let combinedPdf = await PDFDocument.create();
        for (let file of fileData) {
            const { data, options } = file;
            let newPages = await PDFDocument.load(data);
            if (options.appendName) newPages = await appendStudentName(newPages, options.studentName);
            if (options.appendLogo) newPages = await appendLogo(newPages, resGraphic.data);
            if (options.appendSquare) newPages = await appendSquare(newPages, options.squareColor);

            const copiedPages = await combinedPdf.copyPages(newPages, newPages.getPageIndices());
            for (let page of copiedPages) {
                combinedPdf.addPage(page);
            }

            // Append Flag Warning Page if requested
            if (currentLessonIndex === flagPageLessonsOffset) {
                const graphicParams2 = {
                    fileInfo: {
                        fileName: 'flag.png',
                        type: 'graphic'
                    }
                };
                const flagGraphic = await basicPathDownloadND(graphicParams2, path, reqType);
                combinedPdf = await addFlagWarningPage(combinedPdf, flagGraphic.data);
            }
            currentLessonIndex--;
        }


        if (additionalOptions.splitInFours) {
            await splitAndZip(combinedPdf, 4);
        } else {
            const pdfData = await combinedPdf.save();
            openPopup(pdfData);
        }

        return true;
    } else return 'Unexpected error.'
}